/**
 * Chrome/Android meldet „installierbar“ genau einmal (beforeinstallprompt), oft bevor React die
 * Kopfleiste gebaut hat. Ein Listener im Hook verpasste das, und der Installieren-Knopf blieb
 * unsichtbar. Darum wird das Ereignis hier beim Start abgefangen und aufgehoben (index.tsx
 * importiert diese Datei als Erstes); Hinweis und Knopf lesen es über `subscribe`.
 */
import { sendSignupEvent } from 'features/engagement/signup/web-events';

export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((fn) => fn());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
    sendSignupEvent('pwa_installed');
  });
}

export const getDeferredInstallPrompt = () => deferred;

export const subscribeInstallPrompt = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

/** Öffnet den Systemdialog; true, wenn installiert wurde. Danach ist das Ereignis verbraucht. */
export const promptInstall = async (): Promise<boolean> => {
  const event = deferred;
  if (!event) return false;
  deferred = null;
  notify();
  await event.prompt();
  const { outcome } = await event.userChoice;
  return outcome === 'accepted';
};
